/**
 * IDT デザイナー専用 MyPage KPI タブ
 *
 * IDT メンバー (デザイナー) の MyPage「KPI」タブ本体。エンジニア用 MemberDetailContent は使わず、
 * 既存 IDT KPI (idt-kpi) のデータをログイン中デザイナー 1 人にスコープして再構成する。
 *   - 月間pt カード（デザイナー主題色。hover で背景反転。月プルダウンで過去月へ遡り、数字クリックで当該月pt 明細モーダル）
 *   - 四半期KPI「デザインpt」テーブル（月別 目標/実績/達成率 + Q合計。表頭はデザイナー主題色）
 *   - 担当案件一覧（IDチーム看板と同一の active-cases を自分の担当分に絞って表示）
 * 戦闘力/開発pt/release 推移チャート・nickname/github カードは表示しない。
 *
 * Page ノードの boundary アノテーションは本体の export 宣言（DesignerKpiTab）直上に置く。
 */
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { useIdtActiveCases, useIdtKpi, useIdtKpiMonths } from '../../../hooks/useIdtKpi.js';
import { formatNum } from '../combat-power/combat-power-utils.js';
import { CaseTable } from '../idt-kpi/CaseTable.js';
import { colorVarOf, toCaseRows } from '../idt-kpi/shared.js';
import {
  availableMonths,
  availableQuarters,
  jstCurrentMonth,
  monthLabelOf,
} from '../idt-kpi/periods.js';
import type { IdtCategory } from '../../../lib/api.js';
import { DesignerPtDetailModal } from './DesignerPtDetailModal.js';
import { DesignCategoryDonut } from './DesignCategoryDonut.js';
import type { CategoryPtSlice } from './DesignCategoryDonut.js';

/**
 * 4 種別の表示順と塗り色。デザイナー主題色 (--pt-accent) を主色に、同一色系を白で薄めた
 * 濃淡グラデーションで表す（デザイン=主題色そのもの → 情報設計 → チェック → マージ と淡く）。
 * @graph-connects none
 */
const CATEGORY_TINTS: { category: IdtCategory; color: string }[] = [
  { category: 'デザイン', color: 'var(--pt-accent)' },
  { category: '情報設計', color: 'color-mix(in srgb, var(--pt-accent) 70%, white)' },
  { category: 'デザインチェック', color: 'color-mix(in srgb, var(--pt-accent) 45%, white)' },
  { category: 'マスターデザインマージ', color: 'color-mix(in srgb, var(--pt-accent) 25%, white)' },
];

/** @graph-connects none */
interface DesignerKpiTabProps {
  /** ログイン中デザイナーの nickname（大文字小文字混在可。内部で lower-case 化） */
  nickName: string;
}

/** 月別 デザインpt 行（四半期テーブル 1 行ぶん）。 @graph-connects none */
interface DesignMonthRow {
  /** "YYYY-MM" */
  month: string;
  isCurrentMonth: boolean;
  /** 満額目標 (capacity)。null は目標未設定 */
  capacity: number | null;
  /** 按分目標 (asOfTarget)。当月は稼働日進捗で按分済み */
  asOfTarget: number | null;
  /** 実績 = 完了 + 処理中 */
  actual: number;
}

/** "YYYY-MM" → "YYYY/M" @graph-connects none */
function shortMonthLabel(month: string): string {
  return `${String(parseInt(month.slice(0, 4), 10))}/${String(parseInt(month.slice(5, 7), 10))}`;
}

/** 達成率(%)。target<=0 / null は null。 @graph-connects none */
function achievementRate(actual: number, target: number | null): number | null {
  if (target === null || target <= 0) return null;
  return Math.round((actual / target) * 100);
}

/** 達成率バッジの色クラス（100%以上=緑 / 80%以上=黄 / 未満=赤）。 @graph-connects none */
function rateBadgeClass(rate: number): string {
  if (rate >= 100) return 'bg-[#16a34a]/15 text-[#16a34a]';
  if (rate >= 80) return 'bg-[#f59e0b]/15 text-[#b45309]';
  return 'bg-[#ef4444]/15 text-[#ef4444]';
}

/** 達成率バッジ。 @graph-connects none */
function RateBadge({ rate }: { rate: number | null }) {
  if (rate === null) return <>-</>;
  return (
    <span
      className={`inline-flex items-center justify-center font-medium rounded-md px-1.5 py-0.5 text-[11px] leading-none ${rateBadgeClass(rate)}`}
    >
      {`${String(rate)}%`}
    </span>
  );
}

/** 月ラベルのピル（当月はデザイナー主題色塗り、その他は elevated 背景）。 @graph-connects none */
function MonthPill({ month, isCurrent }: { month: string; isCurrent: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ${
        isCurrent ? '' : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
      }`}
      style={
        isCurrent ? { background: 'var(--pt-accent)', color: 'var(--color-bg-surface)' } : undefined
      }
    >
      {shortMonthLabel(month)}
    </span>
  );
}

/**
 * 月間pt カード（選択中の月のデザインpt 実績(処理中込み)をピクセルフォントで表示）。
 * ヘッダーの月プルダウンで過去月へ遡れ、数字ボタンのクリックで当該月の明細モーダルを開く。
 * 数字色・hover 背景はデザイナー主題色 (--pt-accent)。エンジニア版と同じく内部コントロールは反転後も読める。
 * @graph-connects none
 */
function DesignerCurrentMonthPtCard({
  month,
  actual,
  isCurrentMonth,
  accentStyle,
  monthOptions,
  onSelectMonth,
  onOpenDetail,
}: {
  /** 表示中の月 "YYYY-MM"（未取得時は null） */
  month: string | null;
  /** 表示中の月の実績pt（完了 + 処理中）。未取得時は null */
  actual: number | null;
  /** 表示中の月が当月か（タイトル切替用） */
  isCurrentMonth: boolean;
  /** デザイナー主題色 (--pt-accent) を注入する style */
  accentStyle: CSSProperties;
  /** 選択可能な月（当月〜最古月）。空なら月プルダウンを出さない */
  monthOptions: { value: string; label: string }[];
  /** 月プルダウンの選択ハンドラ */
  onSelectMonth: (value: string | null) => void;
  /** 明細モーダルを開くハンドラ */
  onOpenDetail: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card
      className="current-month-pt-card animate-content-reveal flex flex-col md:w-1/4 md:min-w-[180px]"
      style={accentStyle}
    >
      <CardHeader
        title={
          <span data-card-text>
            {isCurrentMonth
              ? t('members.quarterlyKpi.currentMonthPt', '当月pt')
              : t('members.quarterlyKpi.monthPt', '月間pt')}
          </span>
        }
        action={
          monthOptions.length > 0 ? (
            <div className="current-month-pt-actions w-[104px]">
              <Picker
                options={monthOptions}
                value={month}
                onChange={onSelectMonth}
                aria-label={t('members.quarterlyKpi.monthSelect', 'pt 対象月を選択')}
              />
            </div>
          ) : undefined
        }
      />
      <CardContent className="flex flex-1 p-0">
        <button
          type="button"
          data-testid="designer-current-month-pt-card"
          aria-label={t('members.quarterlyKpi.ptDetail.openLabel', '選択月の pt 明細を開く')}
          onClick={onOpenDetail}
          className="pt-open-button flex flex-1 flex-col items-center justify-center gap-3 py-6"
        >
          <div
            className="pt-number text-center leading-none"
            style={{ fontFamily: "'Press Start 2P', monospace" }}
            data-testid="designer-current-month-pt"
          >
            <span className="text-4xl xl:text-5xl text-[color:var(--pt-accent)]">
              {formatNum(actual, 1)}
            </span>
            <span data-card-text className="ml-2 text-sm text-[var(--color-text-secondary)]">
              pt
            </span>
          </div>
          <p data-card-text className="text-[11px] text-[var(--color-text-muted)]">
            {t('members.quarterlyKpi.currentMonthPtNote', '処理中pt を含む')}
          </p>
        </button>
      </CardContent>
    </Card>
  );
}

/** 四半期 デザインpt テーブル（月別 目標/実績/達成率 + Q合計）。表頭はデザイナー主題色。 @graph-connects none */
function DesignPtQuarterTable({ rows }: { rows: DesignMonthRow[] }) {
  const { t } = useTranslation();

  const headerCell = 'py-1.5 px-2.5 text-right text-[11px] font-semibold whitespace-nowrap';
  const cell = 'py-1.5 px-2.5 text-[var(--color-text-primary)] text-right whitespace-nowrap';
  const rowLabel = 'py-1.5 px-2.5 text-left whitespace-nowrap';

  // Q合計: 表示目標は当月のみ按分(asOfTarget)・他月は満額(capacity) の合算（as-of 達成率に整合）
  const totalDisplayTarget = rows.reduce(
    (s, r) => s + (r.isCurrentMonth ? (r.asOfTarget ?? 0) : (r.capacity ?? 0)),
    0,
  );
  const totalActual = rows.reduce((s, r) => s + r.actual, 0);
  const totalRate = achievementRate(
    totalActual,
    totalDisplayTarget > 0 ? totalDisplayTarget : null,
  );

  const renderRow = (label: ReactNode, cells: ReactNode[], isTotal: boolean) => (
    <tr
      className={
        isTotal
          ? 'bg-[var(--color-bg-elevated)] font-bold'
          : 'border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]'
      }
    >
      <th
        scope="row"
        className={`${rowLabel}${isTotal ? ' text-[11px] font-bold text-[var(--color-text-primary)]' : ''}`}
      >
        {label}
      </th>
      {cells.map((c, i) => (
        <td key={i} className={`${cell}${isTotal ? ' font-bold' : ''}`}>
          {c}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="overflow-x-auto h-full" data-testid="designer-quarter-pt-table">
      <table className="w-full h-full text-sm border-collapse">
        <thead>
          {/* 表頭行をデザイナー主題色の薄塗りに */}
          <tr
            className="border-b border-[var(--color-border)]"
            style={{ background: 'color-mix(in srgb, var(--pt-accent) 15%, transparent)' }}
          >
            <th
              className="py-1.5 px-2.5 text-left text-[11px] font-bold tracking-wider whitespace-nowrap"
              style={{ color: 'var(--pt-accent)' }}
            >
              {t('members.quarterlyKpi.designPtSection', 'デザインpt')}
            </th>
            <th className={`${headerCell} text-[var(--color-text-secondary)]`}>
              {t('members.quarterlyKpi.target', '目標')}
            </th>
            <th className={`${headerCell} text-[var(--color-text-secondary)]`}>
              {t('members.quarterlyKpi.actual', '実績')}
            </th>
            <th className={`${headerCell} text-[var(--color-text-secondary)]`}>
              {t('members.quarterlyKpi.achievementRate', '達成率')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const displayTarget = r.isCurrentMonth ? r.asOfTarget : r.capacity;
            return (
              <tr
                key={r.month}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
              >
                <th scope="row" className={rowLabel}>
                  <MonthPill month={r.month} isCurrent={r.isCurrentMonth} />
                </th>
                <td className={cell}>
                  {formatNum(displayTarget, 1)}
                  {r.isCurrentMonth && r.capacity !== null && (
                    <span className="ml-1 text-[10px] font-normal text-[var(--color-text-muted)]">
                      ({formatNum(r.capacity, 1)})
                    </span>
                  )}
                </td>
                <td className={cell}>{formatNum(r.actual, 1)}</td>
                <td className={cell}>
                  <RateBadge rate={achievementRate(r.actual, displayTarget)} />
                </td>
              </tr>
            );
          })}
          {renderRow(
            t('members.quarterlyKpi.qTotal', 'Q合計'),
            [
              formatNum(totalDisplayTarget, 1),
              formatNum(totalActual, 1),
              <RateBadge key="rate" rate={totalRate} />,
            ],
            true,
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT デザイナー MyPage の KPI タブ。月間デザインpt（月選択で過去月へ）・四半期デザインpt表・担当案件一覧を主題色で表示
 * @graph-connects useIdtKpi [calls] 単月 IDT KPI 取得（月間pt カードの選択月 / 種別内訳 donut の選択月）
 * @graph-connects useIdtKpiMonths [calls] 選択四半期 3 ヶ月分の IDT KPI 取得（四半期表用）
 * @graph-connects useIdtActiveCases [calls] 担当案件一覧（未完了・直近1ヶ月更新・期間非依存）取得
 * @graph-connects CaseTable [calls] 担当案件一覧テーブル描画
 * @graph-connects DesignerPtDetailModal [calls] 月間pt 明細モーダル
 */
export function DesignerKpiTab({ nickName }: DesignerKpiTabProps) {
  const { t } = useTranslation();
  const nick = nickName.toLowerCase();
  const colorVar = colorVarOf(nick);
  // CSS カスタムプロパティは Record<string,string> 経由で設定（MemberPicker と同パターン）。
  const accentStyle: CSSProperties = {};
  (accentStyle as Record<string, string>)['--pt-accent'] = `var(${colorVar})`;
  const [detailOpen, setDetailOpen] = useState(false);

  const currentMonth = jstCurrentMonth();
  // 月間pt カードで選択中の月 "YYYY-MM"。既定は当月。過去月を選ぶとその月の実績pt / 明細に切り替わる
  const [ptMonth, setPtMonth] = useState<string>(currentMonth);

  // 四半期セレクタ（新しい順）。既定は当四半期。四半期表 / 種別donut のみがこの選択に追従し、
  // 当月pt カードは常に当月、担当案件一覧は期間非依存を表示する（エンジニア版 MemberQuarterlyKpiTable と同方針）。
  const quarterOptions = availableQuarters(currentMonth);
  const [selectedRepMonth, setSelectedRepMonth] = useState<string>(
    () => quarterOptions[0]?.representativeMonth ?? currentMonth,
  );
  const selectedQuarter =
    quarterOptions.find((q) => q.representativeMonth === selectedRepMonth) ?? quarterOptions[0];
  const quarterMonths = selectedQuarter?.months ?? [currentMonth];

  // 選択四半期: 四半期表 + 種別donut 用
  const quarterResults = useIdtKpiMonths(quarterMonths);
  const quarterLoading = quarterResults.some((r) => r.isLoading);

  const monthRows: DesignMonthRow[] = quarterMonths.map((m, i) => {
    const resp = quarterResults[i]?.data;
    const d = resp?.designers.find((x) => x.nickname.toLowerCase() === nick);
    return {
      month: m,
      isCurrentMonth: m === currentMonth,
      capacity: d?.capacity != null ? Math.round(d.capacity) : null,
      asOfTarget: d?.asOfTarget != null ? Math.round(d.asOfTarget) : null,
      actual: d ? Math.round(d.actualCompleted + d.actualInProgress) : 0,
    };
  });

  const hasAnyKpi = monthRows.some((r) => r.capacity !== null || r.actual > 0);

  // 種別内訳 donut: 月を独立セレクタで選択し、その月の案件を自分の担当分に絞って種別ごとに
  // 実績pt を集計する。既定は当月。塗り色はデザイナー主題色 (--pt-accent) の濃淡。
  const monthOptions = availableMonths(currentMonth);
  const [donutMonth, setDonutMonth] = useState<string>(currentMonth);
  const { data: donutData, isLoading: donutLoading } = useIdtKpi(donutMonth);
  const catTotals = new Map<IdtCategory, number>();
  for (const c of toCaseRows(donutData).filter((x) => x.owner === nick)) {
    catTotals.set(c.category, (catTotals.get(c.category) ?? 0) + c.actual);
  }
  const categorySlices: CategoryPtSlice[] = CATEGORY_TINTS.map((cc) => ({
    category: cc.category,
    pt: Math.round((catTotals.get(cc.category) ?? 0) * 10) / 10,
    color: cc.color,
  }));

  // 担当案件一覧: IDチーム看板 (members/idt-kpi) と同じ期間非依存の active-cases
  // （未完了かつ直近 1 ヶ月以内に更新のある案件）を自分の担当分に絞る。月次 cases
  // (useIdtKpi) は「当月に更新のあった案件」しか含まないため、前月に更新して以降
  // 触っていない未完了案件が月替わりで一覧から消えていた。
  const { data: activeCasesData, isLoading: activeCasesLoading } = useIdtActiveCases();
  const myCases = toCaseRows(activeCasesData).filter((c) => c.owner === nick);

  // 月間pt カード + 明細モーダル用: 選択中の月（既定は当月。過去月は単月 IDT KPI を取得）。
  // ptMonth=donutMonth の間は donut と同一 queryKey のため追加リクエストは発生しない
  const { data: ptData } = useIdtKpi(ptMonth);
  const ptRow = ptData?.designers.find((x) => x.nickname.toLowerCase() === nick);
  const ptActual =
    ptRow != null ? Math.round(ptRow.actualCompleted + ptRow.actualInProgress) : null;
  const ptCases = toCaseRows(ptData).filter((c) => c.owner === nick);
  // 月間pt カードの月プルダウン候補（当月〜最古月。donut と同じ availableMonths を短縮ラベルで）
  const ptMonthOptions = monthOptions.map((m) => ({ value: m, label: shortMonthLabel(m) }));

  const quarterPicker =
    quarterOptions.length > 0 ? (
      <div className="w-[136px]">
        <Picker
          options={quarterOptions.map((q) => ({ value: q.representativeMonth, label: q.label }))}
          value={selectedRepMonth}
          onChange={(value) => {
            if (value !== null) setSelectedRepMonth(value);
          }}
          aria-label={t('members.quarterlyKpi.quarterSelect', '四半期を選択')}
        />
      </div>
    ) : undefined;

  const donutMonthPicker = (
    <div className="w-[120px]">
      <Picker
        options={monthOptions.map((m) => ({ value: m, label: monthLabelOf(m) }))}
        value={donutMonth}
        onChange={(value) => {
          if (value !== null) setDonutMonth(value);
        }}
        aria-label={t('idtKpi.categoryDonut.monthSelect', '月を選択')}
      />
    </div>
  );

  return (
    <div className="space-y-6" style={accentStyle}>
      {/* 当月pt + 四半期デザインpt 表 + 種別構成 donut */}
      <div className="flex flex-col md:flex-row gap-6 items-stretch">
        <DesignerCurrentMonthPtCard
          month={ptMonth}
          actual={ptActual}
          isCurrentMonth={ptMonth === currentMonth}
          accentStyle={accentStyle}
          monthOptions={ptMonthOptions}
          onSelectMonth={(value) => {
            if (value !== null) setPtMonth(value);
          }}
          onOpenDetail={() => {
            setDetailOpen(true);
          }}
        />
        <DesignerPtDetailModal
          cases={ptCases}
          month={ptMonth}
          open={detailOpen}
          onClose={() => {
            setDetailOpen(false);
          }}
        />
        <Card className="animate-content-reveal md:flex-1 min-w-0 flex flex-col">
          <CardHeader title={t('members.quarterlyKpi.title', '四半期KPI')} action={quarterPicker} />
          <CardContent className="flex-1 flex flex-col">
            {quarterLoading ? (
              <div className="flex-1 min-h-[160px] flex items-center justify-center">
                <div className="loading-spinner" />
              </div>
            ) : hasAnyKpi ? (
              <DesignPtQuarterTable rows={monthRows} />
            ) : (
              <p className="flex-1 flex items-center justify-center text-center text-sm text-[var(--color-text-muted)]">
                {t('members.quarterlyKpi.noData', 'データがありません')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* 種別内訳 donut（四半期KPI カードの外に独立カードとして右側へ配置。月セレクタ付き） */}
        <Card className="animate-content-reveal md:w-[260px] md:shrink-0 flex flex-col">
          <CardHeader
            title={t('idtKpi.categoryDonut.title', '種別内訳')}
            action={donutMonthPicker}
          />
          <CardContent className="flex-1 flex flex-col justify-center">
            {donutLoading ? (
              <div className="flex-1 min-h-[160px] flex items-center justify-center">
                <div className="loading-spinner" />
              </div>
            ) : (
              <DesignCategoryDonut slices={categorySlices} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* 担当案件一覧 */}
      <Card className="animate-content-reveal">
        <CardHeader
          title={t('idtKpi.myCasesTitle', '担当案件一覧')}
          description={t(
            'idtKpi.myCasesSubtitle',
            '未完了の担当デザイン案件（直近1ヶ月に更新のあるもの）',
          )}
        />
        <CardContent>
          {activeCasesLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="loading-spinner" />
            </div>
          ) : (
            <CaseTable rows={myCases} hideCompleted resetPageKey="active-cases" rowsPerPage={15} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
